import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, CheckSquare } from 'lucide-react';
import companyService from '../../services/companyService';
import { EmpresaResponseDTO, PageEmpresaResponseDTO } from '../../types/company';

interface CompanyLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (empresaIds: string[]) => Promise<void> | void;
  alreadyLinkedIds: string[];
}

const CompanyLinkModal: React.FC<CompanyLinkModalProps> = ({ isOpen, onClose, onConfirm, alreadyLinkedIds }) => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<PageEmpresaResponseDTO | null>(null);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanies({ page, size, filters: search ? { nomeFantasia: search } : undefined });
      setPageData(data);
    } catch (e) {
      console.error('Erro ao carregar empresas para vínculo:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      void load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, page]);

  const rows = useMemo(() => {
    const list = pageData?.content || [];
    return list.filter(c => !alreadyLinkedIds.includes(c.id));
  }, [pageData, alreadyLinkedIds]);

  const toggle = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold">Vincular empresas</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700"><X className="h-5 w-5"/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome fantasia"
                className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => { setPage(0); void load(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Buscar
            </button>
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selecionar</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome Fantasia</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razão Social</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={4}>Carregando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={4}>Nenhuma empresa encontrada.</td></tr>
                ) : rows.map((c: EmpresaResponseDTO) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input type="checkbox" className="h-4 w-4 text-red-600" checked={selectedIds.includes(c.id)} onChange={() => toggle(c.id)} />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">{c.nomeFantasia}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{c.razaoSocial}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${c.ativo ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'}`}>
                        {c.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageData && pageData.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Anterior</button>
              <span>Página {page + 1} de {pageData.totalPages}</span>
              <button disabled={page + 1 >= pageData.totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Próxima</button>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded border">Cancelar</button>
          <button
            onClick={async () => { await onConfirm(selectedIds); onClose(); }}
            disabled={selectedIds.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
          >
            <CheckSquare className="h-4 w-4"/> Vincular selecionadas
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyLinkModal;